import type { z } from 'zod';

const BASE = import.meta.env['VITE_API_URL'] ?? '/api/v1';

/**
 * El access token vive sólo en memoria; el refresh viaja en cookie httpOnly (SPEC §8.3).
 * Así un XSS no puede robar la sesión larga.
 */
let accessToken: string | null = null;

export function fijarAccessToken(token: string | null): void {
  accessToken = token;
}

export function hayAccessToken(): boolean {
  return accessToken !== null;
}

export class ErrorApi extends Error {
  readonly code: string;
  readonly status: number;
  readonly details?: unknown;

  constructor(status: number, code: string, message: string, details?: unknown) {
    super(message);
    this.name = 'ErrorApi';
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

interface Opciones<T> {
  metodo?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  cuerpo?: unknown;
  schema?: z.ZodType<T>;
  /** Evita el bucle: la propia llamada de refresh no reintenta. */
  sinReintento?: boolean;
}

async function bruto(ruta: string, opciones: Opciones<unknown>): Promise<Response> {
  const headers: Record<string, string> = { Accept: 'application/json' };
  if (opciones.cuerpo !== undefined) headers['Content-Type'] = 'application/json';
  if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`;

  return fetch(`${BASE}${ruta}`, {
    method: opciones.metodo ?? 'GET',
    headers,
    credentials: 'include',
    body: opciones.cuerpo === undefined ? undefined : JSON.stringify(opciones.cuerpo),
  });
}

async function renovarSesion(): Promise<boolean> {
  const resp = await bruto('/auth/refresh', {
    metodo: 'POST',
    cuerpo: { plataforma: 'web' },
    sinReintento: true,
  });
  if (!resp.ok) {
    accessToken = null;
    return false;
  }
  const datos = (await resp.json()) as { accessToken: string };
  accessToken = datos.accessToken;
  return true;
}

export async function api<T = unknown>(ruta: string, opciones: Opciones<T> = {}): Promise<T> {
  let resp = await bruto(ruta, opciones);

  if (resp.status === 401 && !opciones.sinReintento && (await renovarSesion())) {
    resp = await bruto(ruta, opciones);
  }

  if (resp.status === 204) return undefined as T;

  const texto = await resp.text();
  const datos: unknown = texto ? JSON.parse(texto) : null;

  if (!resp.ok) {
    const e = datos as { error?: { code?: string; message?: string; details?: unknown } } | null;
    throw new ErrorApi(
      resp.status,
      e?.error?.code ?? 'INTERNAL_ERROR',
      e?.error?.message ?? 'Error inesperado',
      e?.error?.details,
    );
  }

  return opciones.schema ? opciones.schema.parse(datos) : (datos as T);
}

/** Cierra la sesión local sin esperar al servidor (para 401 definitivos). */
export function olvidarSesion(): void {
  accessToken = null;
}

/** multipart/form-data con el mismo manejo de sesión que `api`. */
export async function apiMultipart<T = unknown>(ruta: string, forma: FormData): Promise<T> {
  const pedir = () =>
    fetch(`${BASE}${ruta}`, {
      method: 'POST',
      credentials: 'include',
      headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
      body: forma,
    });

  let resp = await pedir();
  if (resp.status === 401 && (await renovarSesion())) resp = await pedir();

  const texto = await resp.text();
  const datos: unknown = texto ? JSON.parse(texto) : null;
  if (!resp.ok) {
    const e = datos as { error?: { code?: string; message?: string } } | null;
    throw new ErrorApi(resp.status, e?.error?.code ?? 'INTERNAL_ERROR', e?.error?.message ?? 'Error inesperado');
  }
  return datos as T;
}

/** Descarga binaria autenticada. Devuelve null en 404 (p. ej. credenciales ya usadas). */
export async function apiBlob(ruta: string): Promise<Blob | null> {
  const pedir = () =>
    fetch(`${BASE}${ruta}`, {
      credentials: 'include',
      headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
    });
  let resp = await pedir();
  if (resp.status === 401 && (await renovarSesion())) resp = await pedir();
  if (resp.status === 404) return null;
  if (!resp.ok) throw new ErrorApi(resp.status, 'INTERNAL_ERROR', `Error ${resp.status}`);
  return resp.blob();
}

export { renovarSesion };
